import { useState } from "react";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, Send, Loader2 } from "lucide-react";
import { submitForm, errMessage } from "@/lib/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^[0-9+\-()\s]{7,20}$/;

// Rider application: Name, Email, Phone. Saves to the backend (dedupes by email).
export const RiderApplicationForm = ({
  testIdPrefix = "rider",
  source = "website",
  buttonLabel = "Apply now",
}) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [done, setDone] = useState(false);
  const [resultMsg, setResultMsg] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Please enter your name.");
    if (!EMAIL_RE.test(form.email.trim())) return toast.error("Please enter a valid email address.");
    if (!PHONE_RE.test(form.phone.trim())) return toast.error("Please enter a valid phone number.");
    setSubmitting(true);
    try {
      const res = await submitForm("/rider-applications", {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        source,
      });
      setResultMsg(res?.duplicate ? res.message : "");
      setDone(true);
      toast.success(res?.duplicate ? "You've already applied!" : "Application received!", {
        description: res?.duplicate
          ? "We already have your details on file."
          : "Our team will reach out before launch.",
      });
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        data-testid={`${testIdPrefix}-success`}
        className="flex items-start gap-2 rounded-lg border border-halalgo-green-200 bg-halalgo-green-50 px-4 py-3 text-sm font-medium text-halalgo-green-800"
      >
        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" />
        {resultMsg
          ? resultMsg
          : `Salamat, ${form.name || "rider"}! Your application is in — we'll be in touch soon.`}
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3" data-testid={`${testIdPrefix}-form`} noValidate>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-name`}>Full name</Label>
        <Input id={`${testIdPrefix}-name`} data-testid={`${testIdPrefix}-name`} value={form.name} onChange={update("name")} placeholder="Juan Dela Cruz" className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-email`}>Email</Label>
        <Input id={`${testIdPrefix}-email`} type="email" data-testid={`${testIdPrefix}-email`} value={form.email} onChange={update("email")} placeholder="you@email.com" className="h-11" />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`${testIdPrefix}-phone`}>Phone</Label>
        <Input id={`${testIdPrefix}-phone`} type="tel" data-testid={`${testIdPrefix}-phone`} value={form.phone} onChange={update("phone")} placeholder="+63 900 000 0000" className="h-11" />
      </div>
      <Button
        type="submit"
        disabled={submitting}
        data-testid={`${testIdPrefix}-submit`}
        className="h-11 w-full bg-halalgo-green-600 text-white shadow-lift-sm hover:bg-halalgo-green-700 active:scale-[0.98]"
      >
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        {buttonLabel}
      </Button>
    </form>
  );
};
